<script setup>
    import { ref, watchEffect } from 'vue';
    import { getFromAPI, buildPageFooter, isButtonDisabled } from '../functionCollection';

    const props = defineProps(['fetchUrl', 'editUrl', 'waitingListUrl']);
    const parkobjectList = ref(null);
    const columnsToFetch = "id,name,country,city,postalcode,street,totalcapacity,chargecapacity,maxdp,currUse,free,waiting";

    // for the page footer
    const currentPage = ref(1);
    const maxPageNumber = ref(1);
    const lotsOnPage = ref(null); // lots as in parking lots
    const footerPageNumbers = ref(new Array());

    watchEffect(async () => {
        const url = getFullUrl(props.fetchUrl);
        parkobjectList.value = await getFromAPI(url);

        maxPageNumber.value = parkobjectList.value.meta.last_page; //gets the max page number straight from the API data, thankfully

        parkobjectList.value = parkobjectList.value.data;
        lotsOnPage.value = parkobjectList.value.length;
        footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
    })

    // fills in the URL used for fetching with the needed URL parameters.
    function getFullUrl(url) {
        return url = url.concat('/').concat(`?page=${currentPage.value}&per_page=25&columns=${columnsToFetch}&column=id&order=asc`);
    }

    // just replaces the :id placeholder in the URL with the desired ID.
    function replaceIDinUrl(url, id) {
        let formattedUrl = url.replace(':id', id);
        return formattedUrl;
    }

    // calls new parkobjectList whenever a page change is requested (i.e. a page button is pressed in the footer)
    async function getNewPage(newPage) {
        currentPage.value = newPage;
        if (currentPage.value < 1) {
            currentPage.value = 1;
        }
        parkobjectList.value = await getFromAPI(getFullUrl(props.fetchUrl));
        maxPageNumber.value = parkobjectList.value.meta.last_page;
        parkobjectList.value = parkobjectList.value.data;
        lotsOnPage.value = parkobjectList.value.length;
        buildPageFooter(currentPage.value, maxPageNumber.value);
    }

</script>

<template>
    <div class="main-table" v-if="parkobjectList && parkobjectList.length > 0">
        <div class="inner-table">
            <table class="table">
                <thead>
                    <tr>
                        <th class="table-head">ID</th>
                        <th class="table-head">Name</th>
                        <th class="table-head">Land</th>
                        <th class="table-head">Stadt</th>
                        <th class="table-head">PLZ</th>
                        <th class="table-head">Straße</th>
                        <th class="table-head">Stellplätze</th>
                        <th class="table-head">Ladeplätze</th>
                        <th class="table-head">Max. Dauerparker</th>
                        <th class="table-head">Vermietungen (abzgl. Gruppenzähler)</th>
                        <th class="table-head">Frei</th>
                        <th class="table-head">Auf Warteliste</th>
                        <th class="table-head">Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="m-datatable__row" v-for="parkobject of parkobjectList" :key="parkobject.id">
                        <td>{{ parkobject.id }}</td>
                        <td>{{ parkobject.name }}</td>
                        <td>{{ parkobject.country }}</td>
                        <td>{{ parkobject.city }}</td>
                        <td>{{ parkobject.postalcode }}</td>
                        <td>{{ parkobject.street }}</td>
                        <td>{{ parkobject.totalcapacity }}</td>
                        <td>{{ parkobject.chargecapacity }}</td>
                        <td>{{ parkobject.maxdp }}</td>
                        <td>{{ parkobject.currUse }}</td>
                        <td>{{ parkobject.free }}</td>
                        <td>{{ parkobject.waiting }}</td>
                        <td>
                            <div class="buttons d-grid gap-2">
                                <a  class="btn btn-primary"
                                    :href="replaceIDinUrl(props.editUrl, parkobject.id)"
                                    role="button">
                                    Bearbeiten
                                </a>
                                <a  class="btn btn-primary"
                                    :href="replaceIDinUrl(props.waitingListUrl, parkobject.id)"
                                    role="button">
                                    Warteliste
                                </a>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="table-footer middle" v-if="parkobjectList && parkobjectList.length > 0 && footerPageNumbers.length > 1">
        <ul class="pagination">
            <li class="page-item">
                <button class="page-link" :disabled="isButtonDisabled('start')" @click="getNewPage(currentPage - 1)">
                    <i class="fas fa-lg fa-caret-left"></i>
                </button>
            </li>
            <li v-for="page in footerPageNumbers" :key="page.id" :class="{ 'active' : page == currentPage }" >
                <button class="page-link" @click="getNewPage(page)" :disabled="isButtonDisabled(page)">{{ page }}</button>
            </li>
            <li class="page-item">
                <button class="page-link" @click="getNewPage(currentPage + 1)" :disabled="isButtonDisabled('end')">
                    <i class="fas fa-lg fa-caret-right"></i>
                </button>
            </li>
        </ul>
    </div>

    <div class="m-3" v-if="parkobjectList && (parkobjectList.length == 0 || parkobjectList == null)">
        <div class="alert alert-custom" role="alert">
            <i class="fa-solid fa-comment fa-4x"></i>
            <span>Es wurden keine Parkobjekte gefunden.</span>
        </div>
    </div>

</template>
